import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { useCaptions } from '../context/CaptionContext';
import MiniWaveform from './MiniWaveform';
import { useI18n } from '../../i18n';

// Icons as components
const PlayIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M8 5v14l11-7z" />
  </svg>
);

const PauseIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <rect x="6" y="4" width="4" height="16" rx="1" />
    <rect x="14" y="4" width="4" height="16" rx="1" />
  </svg>
);

const SkipBackIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <polygon points="11 19 2 12 11 5 11 19" />
    <polygon points="22 19 13 12 22 5 22 19" />
  </svg>
);

const SkipForwardIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <polygon points="13 19 22 12 13 5 13 19" />
    <polygon points="2 19 11 12 2 5 2 19" />
  </svg>
);

const VolumeIcon = ({ size = 20, muted = false }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    {muted ? (
      <>
        <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" fill="currentColor" stroke="none" />
        <line x1="23" y1="9" x2="17" y2="15" />
        <line x1="17" y1="9" x2="23" y2="15" />
      </>
    ) : (
      <>
        <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" fill="currentColor" stroke="none" />
        <path d="M19.07 4.93a10 10 0 010 14.14M15.54 8.46a5 5 0 010 7.07" />
      </>
    )}
  </svg>
);

const VideoPreview = () => {
  const { t } = useI18n();
  const audioRef = useRef(null);
  const { 
    currentTime: contextTime, 
    setCurrentTime, 
    videoSrc, 
    registerSeekCallback, 
    registerVideoRef,
    captionData,
    getDisplayText
  } = useCaptions();
  
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const bufferingTimeoutRef = useRef(null);
  const [isSpeedMenuOpen, setIsSpeedMenuOpen] = useState(false);
  const [timeFormat, setTimeFormat] = useState('mmss'); // 'mmss' or 'seconds'
  const [isMobile, setIsMobile] = useState(false);
  const [localTime, setLocalTime] = useState(0); // Local time for smooth progress bar
  const progressAnimationRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const progressBarRef = useRef(null);
  const wasPlayingBeforeDrag = useRef(false);

  // Detect mobile
  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 768px)');
    const handleChange = (event) => setIsMobile(event.matches);
    setIsMobile(mediaQuery.matches);
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
    } else {
      mediaQuery.addListener(handleChange);
    }
    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleChange);
      } else {
        mediaQuery.removeListener(handleChange);
      }
    };
  }, []);

  // Smooth local time update for progress bar (runs independently for smoother UI)
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateLocalTime = () => {
      if (audio && !audio.paused) {
        setLocalTime(audio.currentTime);
      }
      progressAnimationRef.current = requestAnimationFrame(updateLocalTime);
    };

    if (isPlaying) {
      progressAnimationRef.current = requestAnimationFrame(updateLocalTime);
    }

    return () => {
      if (progressAnimationRef.current) {
        cancelAnimationFrame(progressAnimationRef.current);
      }
    };
  }, [isPlaying]);

  // Sync local time when seeking or pausing
  useEffect(() => {
    setLocalTime(contextTime);
  }, [contextTime]);

  // Use ref for duration to avoid stale closures
  const durationRef = useRef(duration);
  
  useEffect(() => {
    durationRef.current = duration;
  }, [duration]);

  // Register seek callback so external components can control audio time
  useEffect(() => {
    registerSeekCallback((time) => {
      const audio = audioRef.current;
      if (audio) {
        audio.currentTime = time;
      }
    });
    
    return () => registerSeekCallback(null);
  }, [registerSeekCallback]);

  // Register audio element reference for timeline editor access
  useEffect(() => {
    if (audioRef.current) {
      registerVideoRef(audioRef.current);
    }
  }, [videoSrc, registerVideoRef]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !videoSrc) return;

    let animationId = null;
    let lastUpdate = 0;
    const UPDATE_INTERVAL = 16; // ~60fps for smooth updates

    // High-frequency time update using requestAnimationFrame
    const updateTime = (timestamp) => {
      if (timestamp - lastUpdate >= UPDATE_INTERVAL) {
        if (!audio.paused) {
          setCurrentTime(audio.currentTime);
        }
        lastUpdate = timestamp;
      }
      animationId = requestAnimationFrame(updateTime);
    };

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
    };

    const handlePlay = () => {
      setIsPlaying(true);
      animationId = requestAnimationFrame(updateTime);
    };
    
    const handlePause = () => {
      setIsPlaying(false);
      if (animationId) {
        cancelAnimationFrame(animationId);
        animationId = null;
      }
    };
    
    const handleEnded = () => {
      setIsPlaying(false);
      if (animationId) {
        cancelAnimationFrame(animationId);
        animationId = null;
      }
    };
    
    const handleSeeked = () => {
      setCurrentTime(audio.currentTime);
      // Clear any pending buffering timeout and hide buffering on successful seek
      if (bufferingTimeoutRef.current) {
        clearTimeout(bufferingTimeoutRef.current);
        bufferingTimeoutRef.current = null;
      }
      setIsBuffering(false);
    };

    // Helper function to check if a time position is already buffered
    const isTimeBuffered = (time) => {
      const buffered = audio.buffered;
      for (let i = 0; i < buffered.length; i++) {
        if (time >= buffered.start(i) && time <= buffered.end(i)) {
          return true;
        }
      }
      return false;
    };

    const handleBufferStart = () => {
      // Clear any existing timeout
      if (bufferingTimeoutRef.current) {
        clearTimeout(bufferingTimeoutRef.current);
      }
      // Add a small delay before showing buffering to avoid flash for quick loads
      bufferingTimeoutRef.current = setTimeout(() => {
        setIsBuffering(true);
      }, 150); // 150ms delay before showing spinner
    };

    const handleBufferEnd = () => {
      // Clear the timeout to prevent delayed buffering state
      if (bufferingTimeoutRef.current) {
        clearTimeout(bufferingTimeoutRef.current);
        bufferingTimeoutRef.current = null;
      }
      setIsBuffering(false);
    };

    // Smart seeking handler - only show buffering if position is not already buffered
    const handleSeeking = () => {
      const seekTarget = audio.currentTime;
      // Only show buffering indicator if the target position is NOT already buffered
      if (!isTimeBuffered(seekTarget)) {
        handleBufferStart();
      }
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('seeked', handleSeeked);
    audio.addEventListener('waiting', handleBufferStart);
    audio.addEventListener('seeking', handleSeeking); // Use smart seeking handler
    audio.addEventListener('stalled', handleBufferStart);
    audio.addEventListener('loadstart', handleBufferStart);
    audio.addEventListener('canplay', handleBufferEnd);
    audio.addEventListener('canplaythrough', handleBufferEnd);
    audio.addEventListener('playing', handleBufferEnd);
    audio.addEventListener('loadeddata', handleBufferEnd);

    if (!audio.paused) {
      animationId = requestAnimationFrame(updateTime);
    }

    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
      // Clear buffering timeout on cleanup
      if (bufferingTimeoutRef.current) {
        clearTimeout(bufferingTimeoutRef.current);
        bufferingTimeoutRef.current = null;
      }
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('seeked', handleSeeked);
      audio.removeEventListener('waiting', handleBufferStart);
      audio.removeEventListener('seeking', handleSeeking);
      audio.removeEventListener('stalled', handleBufferStart);
      audio.removeEventListener('loadstart', handleBufferStart);
      audio.removeEventListener('canplay', handleBufferEnd);
      audio.removeEventListener('canplaythrough', handleBufferEnd);
      audio.removeEventListener('playing', handleBufferEnd);
      audio.removeEventListener('loadeddata', handleBufferEnd);
    };
  }, [setCurrentTime, videoSrc]);

  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
  }, [isPlaying]);

  const skipBackward = () => {
    const audio = audioRef.current;
    if (!audio) return;
    
    // Get all non-silence words sorted by start time
    const words = (captionData?.words || [])
      .filter(w => w.type !== 'silence' && w.type !== 'punctuation')
      .sort((a, b) => a.start_time - b.start_time);
    
    if (words.length === 0) {
      // Fallback to 5s skip if no words
      audio.currentTime = Math.max(0, audio.currentTime - 5);
      return;
    }
    
    // Find the previous word (word that starts before current time - small buffer)
    const currentTime = audio.currentTime;
    const buffer = 0.1; // Small buffer to avoid getting stuck on same word
    
    let prevWord = null;
    for (let i = words.length - 1; i >= 0; i--) {
      if (words[i].start_time < currentTime - buffer) {
        prevWord = words[i];
        break;
      }
    }
    
    if (prevWord) {
      audio.currentTime = prevWord.start_time;
    } else {
      // Go to beginning if no previous word
      audio.currentTime = 0;
    }
  };

  const skipForward = () => {
    const audio = audioRef.current;
    if (!audio) return;
    
    // Get all non-silence words sorted by start time
    const words = (captionData?.words || [])
      .filter(w => w.type !== 'silence' && w.type !== 'punctuation')
      .sort((a, b) => a.start_time - b.start_time);
    
    if (words.length === 0) {
      // Fallback to 5s skip if no words
      audio.currentTime = Math.min(duration, audio.currentTime + 5);
      return;
    }
    
    // Find the next word (word that starts after current time)
    const currentTime = audio.currentTime;
    
    let nextWord = null;
    for (let i = 0; i < words.length; i++) {
      if (words[i].start_time > currentTime + 0.01) {
        nextWord = words[i];
        break;
      }
    }
    
    if (nextWord) {
      audio.currentTime = nextWord.start_time;
    } else {
      // Go to end if no next word
      audio.currentTime = duration;
    }
  };

  const toggleMute = () => {
    const audio = audioRef.current;
    if (audio) {
      audio.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const changePlaybackSpeed = (rate) => {
    const audio = audioRef.current;
    if (audio) {
      audio.playbackRate = rate;
      setPlaybackSpeed(rate);
    }
    setIsSpeedMenuOpen(false);
  };

  const handleSeek = (time) => {
    const audio = audioRef.current;
    if (audio) {
      audio.currentTime = time;
      setCurrentTime(time);
    }
  };

  const formatTime = (seconds) => {
    if (isNaN(seconds)) return timeFormat === 'seconds' ? '0.000s' : '00:00.00';
    
    if (timeFormat === 'seconds') {
      // Show as seconds with milliseconds (e.g., 125.450s)
      return `${seconds.toFixed(3)}s`;
    }
    
    // Default MM:SS.cc format
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    const centisecs = Math.floor((seconds % 1) * 100);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}.${centisecs.toString().padStart(2, '0')}`;
  };

  const toggleTimeFormat = () => {
    setTimeFormat(prev => prev === 'mmss' ? 'seconds' : 'mmss');
  };

  const nextWordPreview = useMemo(() => {
    const words = (captionData?.words || [])
      .filter(w => w.type !== 'silence' && w.type !== 'punctuation')
      .sort((a, b) => a.start_time - b.start_time);
    if (!words.length) return t('No words');
    const buffer = 0.01;
    const next = words.find(w => w.start_time > contextTime + buffer);
    return next ? getDisplayText(next) : t('End');
  }, [captionData, contextTime, getDisplayText, t]);

  // Calculate time from position on progress bar
  const getTimeFromPosition = useCallback((clientX) => {
    if (!progressBarRef.current || duration <= 0) return 0;
    const rect = progressBarRef.current.getBoundingClientRect();
    const percent = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    return percent * duration;
  }, [duration]);

  // Handle drag start (mouse/touch)
  const handleDragStart = useCallback((clientX) => {
    const audio = audioRef.current;
    // Store if audio was playing and pause it
    wasPlayingBeforeDrag.current = audio && !audio.paused;
    if (audio && !audio.paused) {
      audio.pause();
    }
    setIsDragging(true);
    const time = getTimeFromPosition(clientX);
    setLocalTime(time);
  }, [getTimeFromPosition]);

  // Handle drag move
  const handleDragMove = useCallback((clientX) => {
    if (!isDragging) return;
    const time = getTimeFromPosition(clientX);
    setLocalTime(time);
  }, [isDragging, getTimeFromPosition]);

  // Handle drag end
  const handleDragEnd = useCallback((clientX) => {
    if (!isDragging) return;
    setIsDragging(false);
    const time = getTimeFromPosition(clientX);
    handleSeek(time);
    // Resume playback if it was playing before drag
    const audio = audioRef.current;
    if (audio && wasPlayingBeforeDrag.current) {
      audio.play();
    }
  }, [isDragging, getTimeFromPosition, handleSeek]);

  // Mouse events for progress bar dragging
  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e) => {
      e.preventDefault();
      handleDragMove(e.clientX);
    };

    const handleMouseUp = (e) => {
      handleDragEnd(e.clientX);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleDragMove, handleDragEnd]);

  // Touch events for progress bar dragging
  useEffect(() => {
    if (!isDragging) return;

    const handleTouchMove = (e) => {
      if (e.touches.length > 0) {
        handleDragMove(e.touches[0].clientX);
      }
    };

    const handleTouchEnd = (e) => {
      const touch = e.changedTouches[0];
      if (touch) {
        handleDragEnd(touch.clientX);
      }
    };

    window.addEventListener('touchmove', handleTouchMove, { passive: true });
    window.addEventListener('touchend', handleTouchEnd);
    window.addEventListener('touchcancel', handleTouchEnd);

    return () => {
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
      window.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, [isDragging, handleDragMove, handleDragEnd]);

  // Early return if no video source
  if (!videoSrc) return null;

  // Compact time format for mobile
  const formatTimeMobile = (seconds) => {
    if (isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Progress percentage for mobile progress bar (use localTime for smooth updates)
  const progressPercent = duration > 0 ? (localTime / duration) * 100 : 0;

  // Mobile compact player
  if (isMobile) {
    return (
      <>
        {/* Hidden Audio Element */}
        <audio
          ref={audioRef}
          src={videoSrc}
          style={{ display: 'none' }}
        >
          {t('Your browser does not support the audio tag.')}
        </audio>

        {/* Compact Mobile Player */}
        <div className="fixed bottom-4 left-3 right-3 z-50 pointer-events-none" dir="ltr">
          <div className="pointer-events-auto backdrop-blur-xl border ring-1 rounded-2xl bg-white/95 border-white/60 ring-slate-900/5 shadow-[0_16px_48px_-12px_rgba(0,0,0,0.18)] overflow-hidden">
            {/* Progress Bar - Draggable */}
            <div 
              ref={progressBarRef}
              className="h-1.5 bg-slate-200 cursor-pointer touch-none"
              onClick={(e) => {
                if (!isDragging) {
                  const time = getTimeFromPosition(e.clientX);
                  handleSeek(time);
                }
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                handleDragStart(e.clientX);
              }}
              onTouchStart={(e) => {
                if (e.touches.length > 0) {
                  handleDragStart(e.touches[0].clientX);
                }
              }}
            >
              <div 
                className="h-full bg-brand"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
            
            {/* Controls */}
            <div className="px-3 py-2 flex items-center gap-2">
              {/* Skip Back */}
              <button 
                onClick={skipBackward} 
                className="w-9 h-9 flex items-center justify-center rounded-full transition-all active:scale-90 text-slate-400 active:text-brand"
                aria-label={t('Previous word')}
              >
                <SkipBackIcon size={16} />
              </button>
              
              {/* Play/Pause */}
              <button 
                onClick={togglePlay} 
                className="w-11 h-11 text-white rounded-full flex items-center justify-center transition-all shadow-lg active:scale-95 bg-slate-900 active:bg-brand shadow-slate-900/20"
                aria-busy={isBuffering ? 'true' : 'false'}
              >
                {isBuffering ? (
                  <div className="w-5 h-5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                ) : isPlaying ? (
                  <PauseIcon size={20} />
                ) : (
                  <PlayIcon size={20} />
                )}
              </button>
              
              {/* Skip Forward */}
              <button 
                onClick={skipForward} 
                className="w-9 h-9 flex items-center justify-center rounded-full transition-all active:scale-90 text-slate-400 active:text-brand"
                aria-label={t('Next word')}
              >
                <SkipForwardIcon size={16} />
              </button>

              {/* Time Display */}
              <div className="flex-1 text-center">
                <span className="text-xs font-mono font-semibold text-slate-700">
                  {formatTimeMobile(localTime)}
                </span>
                <span className="text-xs font-mono text-slate-300 mx-1">/</span>
                <span className="text-xs font-mono text-slate-400">
                  {formatTimeMobile(duration)}
                </span>
              </div>

              {/* Speed */}
              <button 
                onClick={() => setIsSpeedMenuOpen(!isSpeedMenuOpen)} 
                className={`text-[11px] font-bold px-2 h-8 flex items-center justify-center rounded-lg transition-all ${
                  isSpeedMenuOpen 
                    ? 'bg-slate-100 text-slate-900' 
                    : 'text-slate-500 active:bg-slate-100'
                }`}
              >
                {playbackSpeed}x
              </button>

              {/* Volume */}
              <button 
                onClick={toggleMute} 
                className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 active:text-slate-900 active:bg-slate-100"
              >
                <VolumeIcon size={18} muted={isMuted} />
              </button>
            </div>
          </div>
        </div>

        {/* Speed Menu - Fixed overlay for mobile */}
        {isSpeedMenuOpen && (
          <div className="fixed inset-0 z-[100] flex items-end justify-center">
            <div 
              className="absolute inset-0 bg-black/30" 
              onClick={() => setIsSpeedMenuOpen(false)} 
            />
            <div className="relative bg-white rounded-t-2xl w-full max-w-md p-4 pb-6 shadow-xl animate-[slideUp_0.2s_ease-out]">
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm font-bold text-slate-700">{t('Playback Speed')}</span>
                <button 
                  onClick={() => setIsSpeedMenuOpen(false)}
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 text-slate-500"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {[0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2].map((rate) => (
                  <button 
                    key={rate} 
                    onClick={() => changePlaybackSpeed(rate)} 
                    className={`py-3 rounded-xl text-sm font-bold transition-all ${
                      playbackSpeed === rate 
                        ? 'bg-brand text-white shadow-lg shadow-brand/20' 
                        : 'bg-slate-100 text-slate-600 active:bg-slate-200'
                    }`}
                  >
                    {rate}x
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  // Desktop player (original)
  return (
    <>
      {/* Hidden Audio Element */}
      <audio
        ref={audioRef}
        src={videoSrc}
        style={{ display: 'none' }}
      >
        {t('Your browser does not support the audio tag.')}
      </audio>

      {/* Floating Player */}
      <div className="absolute bottom-8 left-0 right-0 flex justify-center z-50 pointer-events-none" dir="ltr">
        <div 
          data-tour="audio-player"
          className="pointer-events-auto w-[95%] md:max-w-5xl h-[5.5rem] backdrop-blur-xl border ring-1 rounded-[2.5rem] px-4 md:px-8 flex items-center gap-6 transition-all duration-500 ease-out hover:scale-[1.005] bg-white/95 border-white/60 ring-slate-900/5 shadow-[0_32px_64px_-12px_rgba(0,0,0,0.14)]">
          
          {/* Play Controls */}
          <div className="flex items-center gap-3 shrink-0">
            <button 
              onClick={skipBackward} 
              className="w-10 h-10 flex items-center justify-center rounded-full transition-all active:scale-90 text-slate-400 hover:text-brand hover:bg-brand/10 tooltip-trigger"
              data-tooltip={t('Previous word')}
              aria-label={t('Previous word')}
            >
              <SkipBackIcon />
            </button>
            
            <button 
              onClick={togglePlay} 
              className="w-14 h-14 text-white rounded-full flex items-center justify-center transition-all shadow-xl hover:scale-105 active:scale-95 group bg-slate-900 hover:bg-brand shadow-slate-900/20 hover:shadow-brand/40"
              aria-busy={isBuffering ? 'true' : 'false'}
            >
              {isBuffering ? (
                <div className="w-6 h-6 border-2 border-white/40 border-t-white rounded-full animate-spin" />
              ) : isPlaying ? (
                <PauseIcon size={24} />
              ) : (
                <PlayIcon size={24} />
              )}
            </button>
            
            <button 
              onClick={skipForward} 
              className="w-10 h-10 flex items-center justify-center rounded-full transition-all active:scale-90 text-slate-400 hover:text-brand hover:bg-brand/10 tooltip-trigger"
              data-tooltip={t('Next: {word}', { word: nextWordPreview })}
              aria-label={t('Next word')}
            >
              <SkipForwardIcon />
            </button>
          </div>

          {/* Waveform Section */}
          <div className="flex-1 flex flex-col justify-center h-full py-2 min-w-0 relative group px-2">
            <MiniWaveform 
              audioSrc={videoSrc}
              currentTime={contextTime}
              duration={duration}
              onSeek={handleSeek}
            />
          </div>

          {/* Time Display - Click to toggle format */}
          <button 
            onClick={toggleTimeFormat}
            className="flex items-center justify-center gap-0.5 text-[11px] font-mono font-bold shrink-0 select-none px-3 py-2 rounded-xl transition-colors active:opacity-70 hover:bg-slate-100/80 tooltip-trigger"
            data-tooltip={t('Toggle time format')}
            aria-label={t('Toggle time format')}
          >
            <span className="text-slate-900">{formatTime(contextTime)}</span>
            <span className="text-slate-300 mx-0.5">/</span>
            <span className="text-slate-400">{formatTime(duration)}</span>
          </button>

          {/* Right Controls */}
          <div className="flex items-center gap-4 shrink-0 pl-4 border-l h-10 border-slate-100/80">
            {/* Speed Selector */}
            <div className="relative">
              <button 
                onClick={() => setIsSpeedMenuOpen(!isSpeedMenuOpen)} 
                className={`text-xs font-bold w-10 h-10 flex items-center justify-center rounded-2xl transition-all border ${
                  isSpeedMenuOpen 
                    ? 'bg-slate-100 text-slate-900 border-slate-200' 
                    : 'text-slate-500 hover:text-slate-900 hover:bg-slate-50 border-transparent hover:border-slate-100'
                }`}
              >
                {playbackSpeed}x
              </button>
              
              {isSpeedMenuOpen && (
                <>
                  <div 
                    className="fixed inset-0 z-[55]" 
                    onClick={() => setIsSpeedMenuOpen(false)} 
                  />
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 z-[60]">
                    <div className="backdrop-blur-2xl rounded-2xl shadow-[0_8px_32px_-4px_rgba(0,0,0,0.12)] border p-2 flex flex-col gap-1 animate-popIn min-w-[80px] origin-bottom ring-1 bg-white/80 border-white/50 ring-slate-900/5">
                      {[2, 1.5, 1.25, 1, 0.75, 0.5, 0.35, 0.25].map((rate) => (
                        <button 
                          key={rate} 
                          onClick={() => changePlaybackSpeed(rate)} 
                          className={`px-2 py-2.5 text-xs font-bold rounded-xl transition-all w-full flex items-center justify-center ${
                            playbackSpeed === rate 
                              ? 'bg-brand text-white shadow-md shadow-brand/20 scale-105 font-extrabold' 
                              : 'text-slate-500 hover:bg-slate-100/80 hover:text-slate-900'
                          }`}
                        >
                          {rate}x
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Volume */}
            <button 
              onClick={toggleMute} 
              className="w-9 h-9 flex items-center justify-center rounded-xl transition-colors text-slate-400 hover:text-slate-900 hover:bg-slate-50"
            >
              <VolumeIcon muted={isMuted} />
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default VideoPreview;
